/* istanbul ignore file */
import { FormSection, BigFormRow, MediumFormRow } from 'framework/ui/layout';
import {
  CREATE_NEW_OFFER_TOOLTIP,
  isFilled,
  OfferCompensationDetailsInput,
  OfferCompensationDetailsListInput,
} from 'domain/shared/offerCompensation';
import { DocumentScanUploaderInput } from 'domain/shared/employee';
import { InputListTemplateProps, TextInput, WysiwygInput, EnumDropdown, useFormContext } from 'framework/form';
import { getTechnologyFilter, TechnologyDropdown } from 'domain/shared/career';
import { PositionDropdown, isSoftwareEngineerOrEducationalSpecialist } from 'domain/shared/employee';
import { useCallback, useRef } from 'react';
import { EmployeeOfferFormData } from 'domain/people/employee/offers/shared/offerTypes';

const COMPETENCY_LEVEL = {
  L0: 'L0',
  L1: 'L1',
  L2: 'L2',
  L3: 'L3',
  L4: 'L4',
  L5: 'L5',
  L6: 'L6',
} as const;

const EmployeeOfferForm = ({ isUpdate = false }: { isUpdate?: boolean }) => {
  const { data } = useFormContext();
  const { positionId } = data || {};
  const initialDataRef = useRef<EmployeeOfferFormData>((data || {}) as EmployeeOfferFormData);
  const initialData = initialDataRef.current;
  const CompensationDetailsInput = useCallback(
    (props: InputListTemplateProps) => (
      <OfferCompensationDetailsInput
        {...props}
        initialValue={initialDataRef.current.offerCompensations?.[props.index]}
        isUpdate={isUpdate}
      />
    ),
    [isUpdate],
  );

  return (
    <FormSection>
      <MediumFormRow>
        <PositionDropdown source="positionId" label="Position" disabled={isUpdate} tooltip={CREATE_NEW_OFFER_TOOLTIP} />
        <TechnologyDropdown
          source="technologyId"
          label="Technology"
          isVisible={offer => isSoftwareEngineerOrEducationalSpecialist(offer?.positionId)}
          customReduce={getTechnologyFilter(positionId)}
          deps={[positionId]}
          disabled={isUpdate}
          tooltip={CREATE_NEW_OFFER_TOOLTIP}
        />
        <EnumDropdown
          options={COMPETENCY_LEVEL}
          source="competencyLevel"
          label="Competency level"
          disabled={isUpdate}
          tooltip={CREATE_NEW_OFFER_TOOLTIP}
        />
        <TextInput
          source="contractDuration"
          label="Contract duration"
          disabled={isUpdate}
          tooltip={CREATE_NEW_OFFER_TOOLTIP}
        />
      </MediumFormRow>

      <BigFormRow>
        <OfferCompensationDetailsListInput
          source="offerCompensations"
          maxFields={4}
          addText="Add compensation details"
          inputTemplate={CompensationDetailsInput}
        />
      </BigFormRow>

      <BigFormRow>
        <WysiwygInput
          source="additionalInformation"
          label="Additional information"
          disabled={isUpdate && isFilled(initialData.additionalInformation)}
          tooltip={isUpdate && isFilled(initialData.additionalInformation) ? CREATE_NEW_OFFER_TOOLTIP : undefined}
        />
      </BigFormRow>

      <BigFormRow>
        <DocumentScanUploaderInput
          label="Attachments"
          source="attachmentIds"
          disabled={isUpdate && isFilled(initialData.attachmentIds)}
          tooltip={isUpdate && isFilled(initialData.attachmentIds) ? CREATE_NEW_OFFER_TOOLTIP : undefined}
        />
      </BigFormRow>
    </FormSection>
  );
};

export default EmployeeOfferForm;
